import React from 'react';
import { Phone, Shield, Award, CheckCircle, Wrench, Clock, Star } from 'lucide-react';
import Marquee from 'react-fast-marquee';

const Hero = () => {
  return (
    <>
      {/* PREMIUM HERO SECTION - Million Dollar Design */}
      <section className="relative min-h-[90vh] flex items-center bg-[#050505] overflow-hidden">
        {/* Background Image with Dark Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_trusted-garage-pro/artifacts/o2p04m7r_WhatsApp%20Image%202026-06-19%20at%201.27.55%20AM.jpeg"
            alt="Premium garage door installation"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/95 via-[#050505]/70 to-transparent"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <div className="max-w-3xl">
            {/* Premium Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0F0F11]/80 border border-[#D4AF37]/30 backdrop-blur-sm mb-8">
              <Star className="text-[#D4AF37] fill-[#D4AF37]" size={20} />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">
                Family Owned Local Business
              </span>
            </div>

            {/* Million Dollar Headline */}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-tight text-white mb-6">
              Saskatoon's Most Trusted Garage Door Service
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl leading-relaxed text-neutral-300 font-light mb-10">
              Elevating your home's exterior with premium installations, rapid emergency repairs, and uncompromising craftsmanship. Background-checked professionals with 8+ years of excellence.
            </p>

            {/* Premium CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <a 
                href="/quote"
                data-testid="hero-cta-quote"
                className="group inline-flex items-center gap-3 px-8 py-5 bg-[#D4AF37] hover:bg-[#F0C84A] text-[#050505] font-bold text-lg rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-[0_8px_32px_rgba(212,175,55,0.3)]"
              >
                Get Free Quote
                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>

              <a 
                href="tel:306-262-5995"
                data-testid="hero-cta-call"
                className="group inline-flex items-center gap-3 px-8 py-5 bg-white/5 hover:bg-white/10 backdrop-blur-xl border border-white/10 hover:border-[#D4AF37]/50 text-white font-bold text-lg rounded-xl transition-all duration-300"
              >
                <Phone size={24} />
                Call 306-262-5995
              </a>
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12 pt-8 border-t border-white/10">
              <div className="flex flex-col">
                <div className="text-3xl font-black text-[#D4AF37] mb-1">8+</div>
                <div className="text-sm text-neutral-400">Years Experience</div>
              </div>
              <div className="flex flex-col">
                <div className="text-3xl font-black text-[#D4AF37] mb-1">25K+</div>
                <div className="text-sm text-neutral-400">Cycle Springs</div>
              </div>
              <div className="flex flex-col">
                <div className="text-3xl font-black text-[#D4AF37] mb-1">24/7</div>
                <div className="text-sm text-neutral-400">Emergency</div>
              </div>
              <div className="flex flex-col">
                <div className="text-3xl font-black text-[#D4AF37] mb-1">100%</div>
                <div className="text-sm text-neutral-400">Warranted</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Premium Trust Marquee */}
      <div className="py-6 bg-[#0F0F11] border-y border-white/5" data-testid="trust-marquee">
        <Marquee gradient={false} speed={40}>
          <div className="flex items-center gap-12 px-6">
            <div className="flex items-center gap-3">
              <Shield className="text-[#D4AF37] fill-[#D4AF37]" size={20} />
              <span className="text-sm font-semibold uppercase tracking-[0.15em] text-[#D4AF37]">Background Checked</span>
            </div>
            <div className="flex items-center gap-3">
              <Award className="text-[#D4AF37]" size={20} />
              <span className="text-sm font-semibold uppercase tracking-[0.15em] text-[#D4AF37]">8+ Years Experience</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="text-[#D4AF37]" size={20} />
              <span className="text-sm font-semibold uppercase tracking-[0.15em] text-[#D4AF37]">25,000+ Cycles</span>
            </div>
            <div className="flex items-center gap-3">
              <Shield className="text-[#D4AF37] fill-[#D4AF37]" size={20} />
              <span className="text-sm font-semibold uppercase tracking-[0.15em] text-[#D4AF37]">Full Warranty</span>
            </div>
            <div className="flex items-center gap-3">
              <Star className="text-[#D4AF37] fill-[#D4AF37]" size={20} />
              <span className="text-sm font-semibold uppercase tracking-[0.15em] text-[#D4AF37]">Family Owned Local Business</span>
            </div>
            <div className="flex items-center gap-3">
              <Clock className="text-[#D4AF37]" size={20} />
              <span className="text-sm font-semibold uppercase tracking-[0.15em] text-[#D4AF37]">24/7 Emergency Service</span>
            </div>
          </div>
        </Marquee>
      </div>

      {/* Premium Services Section */}
      <section className="py-24 lg:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-4 block">
              Uncompromising Quality
            </span>
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-white mb-6">
              Premium Service & Craftsmanship
            </h2>
            <p className="text-lg text-neutral-300 max-w-2xl mx-auto">
              Every installation backed by our commitment to excellence and customer satisfaction
            </p>
          </div>

          {/* Premium Service Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            <div className="group p-8 lg:p-12 bg-[#0F0F11]/50 backdrop-blur-sm border border-white/5 rounded-2xl hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(212,175,55,0.1)] transition-all duration-300">
              <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-6">
                <Wrench className="text-[#D4AF37]" size={28} />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-3">Installation</h3>
              <p className="text-neutral-400 leading-relaxed">
                Professional installation of premium Garaga doors with precision and care. Galvanized steel construction with R-16 insulation.
              </p>
            </div>

            <div className="group p-8 lg:p-12 bg-[#0F0F11]/50 backdrop-blur-sm border border-white/5 rounded-2xl hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(212,175,55,0.1)] transition-all duration-300">
              <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-6">
                <Clock className="text-[#D4AF37]" size={28} />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-3">Emergency Repair</h3>
              <p className="text-neutral-400 leading-relaxed">
                24/7 emergency service for urgent repairs. Fast response times with professional-grade parts and 25,000+ cycle springs.
              </p>
            </div>

            <div className="group p-8 lg:p-12 bg-[#0F0F11]/50 backdrop-blur-sm border border-white/5 rounded-2xl hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(212,175,55,0.1)] transition-all duration-300">
              <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-6">
                <Shield className="text-[#D4AF37]" size={28} />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-3">Maintenance</h3>
              <p className="text-neutral-400 leading-relaxed">
                Regular maintenance programs to extend door life. Complete warranty on parts, labor, and installation.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Hero;
